'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  approveBusinessManualRevenueEntry,
  createBusinessManualRevenueEntry,
  getBusinessManualRevenueEntries,
  getBusinessManualRevenueEntry,
  getBusinessManualRevenueSourceOptions,
  getBusinessReportCenters,
  rejectBusinessManualRevenueEntry,
  submitBusinessManualRevenueEntry,
  updateBusinessManualRevenueEntry,
  type BusinessManualRevenueEntry,
  type BusinessManualRevenueEntryInput,
  type BusinessManualRevenueSourceOption,
  type BusinessReportCenter,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';
import {
  cleanSpaces,
  dateOnly,
  dateTime,
  errorMessage,
  exportCsv,
  FinanceField,
  FinanceModal,
  FinanceStatus,
  formatMoney,
  label,
} from './BusinessReportFinanceUi';

type EntryForm = {
  businessDate: string;
  centerCode: string;
  revenueSourceKey: string;
  amount: string;
  currency: string;
  adjustmentCategory: string;
  reason: string;
  externalReference: string;
  attachmentReference: string;
  relatedRevenueFactId: string;
};

const EMPTY_FORM: EntryForm = {
  businessDate: '',
  centerCode: '',
  revenueSourceKey: 'MANUAL_ADJUSTMENT',
  amount: '',
  currency: '',
  adjustmentCategory: '',
  reason: '',
  externalReference: '',
  attachmentReference: '',
  relatedRevenueFactId: '',
};

export function BusinessManualRevenueEntriesTab({
  active,
  token,
  userName,
  canApprove,
  canManage,
  centerAccess,
  allCenterAccess,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  userName: string;
  canApprove: boolean;
  canManage: boolean;
  centerAccess: string[];
  allCenterAccess: boolean;
  onTopBarActionsChange?: (actions: ReactNode) => void;
}) {
  const [entries, setEntries] = useState<BusinessManualRevenueEntry[]>([]);
  const [centers, setCenters] = useState<BusinessReportCenter[]>([]);
  const [sources, setSources] = useState<BusinessManualRevenueSourceOption[]>([]);
  const [centerCode, setCenterCode] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<BusinessManualRevenueEntry | null | undefined>(undefined);
  const [form, setForm] = useState<EntryForm>(EMPTY_FORM);
  const [detail, setDetail] = useState<BusinessManualRevenueEntry | null>(null);
  const [rejecting, setRejecting] = useState<BusinessManualRevenueEntry | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const scopedCenters = useMemo(() => centers.filter((center) => (
    center.isActive
    && center.approvalStatus === 'APPROVED'
    && center.accessCenterName.toLowerCase() !== 'management'
    && (allCenterAccess || centerAccess.includes(center.accessCenterName))
  )), [allCenterAccess, centerAccess, centers]);

  const manualSources = useMemo(() => sources.filter((source) => source.sourceMode === 'MANUAL'), [sources]);
  const activeManualSources = useMemo(
    () => manualSources.filter((source) => source.isActive),
    [manualSources],
  );
  const selectedManualSource = activeManualSources.find((source) => source.sourceKey === form.revenueSourceKey);
  const eligibleEntryCenters = useMemo(() => {
    if (!selectedManualSource) return [];
    const mappedCodes = new Set(selectedManualSource.applicableCenters
      .filter((mapping) => mapping.isActive)
      .map((mapping) => mapping.center.centerCode));
    return scopedCenters.filter((center) => mappedCodes.has(center.centerCode));
  }, [scopedCenters, selectedManualSource]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entryRows, centerRows, sourceRows] = await Promise.all([
        getBusinessManualRevenueEntries(token, {
          centerCode,
          status,
          businessDateFrom: from,
          businessDateTo: to,
          take: 500,
        }),
        getBusinessReportCenters(token, { status: 'ACTIVE' }),
        getBusinessManualRevenueSourceOptions(token),
      ]);
      setEntries(entryRows);
      setCenters(centerRows);
      setSources(sourceRows);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load Manual Revenue Entries', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [centerCode, from, status, to, token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  useEffect(() => {
    if (centerCode !== 'ALL' && !scopedCenters.some((center) => center.centerCode === centerCode)) {
      setCenterCode('ALL');
    }
  }, [centerCode, scopedCenters]);

  useEffect(() => {
    if (
      editing !== undefined
      && form.centerCode
      && !eligibleEntryCenters.some((center) => center.centerCode === form.centerCode)
    ) {
      setForm((current) => ({ ...current, centerCode: '' }));
    }
  }, [editing, eligibleEntryCenters, form.centerCode]);

  const pending = useMemo(() => entries.filter((entry) => entry.status === 'PENDING'), [entries]);
  const pendingTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of pending) {
      totals.set(entry.currency, (totals.get(entry.currency) || 0) + Number(entry.amount || 0));
    }
    return [...totals.entries()].sort(([left], [right]) => left.localeCompare(right));
  }, [pending]);
  const newEntryAvailable = canManage && activeManualSources.some((source) => source.applicableCenters.some((mapping) => (
    mapping.isActive && scopedCenters.some((center) => center.centerCode === mapping.center.centerCode)
  )));

  const openCreate = useCallback(() => {
    if (!newEntryAvailable) {
      setAlert({
        type: 'info',
        title: 'Manual entry remains disabled',
        message: 'Finance must approve and an administrator must activate a MANUAL Revenue Source for at least one permitted centre.',
      });
      return;
    }
    const firstSource = activeManualSources.find((source) => source.applicableCenters.some((mapping) => (
      mapping.isActive && scopedCenters.some((center) => center.centerCode === mapping.center.centerCode)
    )))!;
    const firstCenter = scopedCenters.find((center) => firstSource.applicableCenters.some((mapping) => mapping.isActive && mapping.center.centerCode === center.centerCode));
    setForm({
      ...EMPTY_FORM,
      businessDate: new Date().toISOString().slice(0, 10),
      centerCode: firstCenter?.centerCode || '',
      revenueSourceKey: firstSource.sourceKey,
    });
    setEditing(null);
  }, [activeManualSources, newEntryAvailable, scopedCenters]);

  const openEdit = (entry: BusinessManualRevenueEntry) => {
    setForm({
      businessDate: dateOnly(entry.businessDate),
      centerCode: entry.center.centerCode,
      revenueSourceKey: entry.revenueSource.sourceKey,
      amount: String(entry.amount),
      currency: entry.currency,
      adjustmentCategory: entry.adjustmentCategory,
      reason: entry.reason,
      externalReference: entry.externalReference || '',
      attachmentReference: entry.attachmentReference || '',
      relatedRevenueFactId: entry.relatedRevenueFactId || '',
    });
    setEditing(entry);
  };

  const save = async () => {
    setLoading(true);
    try {
      const input = entryInput(form);
      if (editing) await updateBusinessManualRevenueEntry(editing.id, input, token);
      else await createBusinessManualRevenueEntry(input, token);
      setEditing(undefined);
      await load();
      setAlert({ type: 'success', title: editing ? 'Draft updated' : 'Draft created', message: 'The manual adjustment remains a separate draft until it is submitted and approved.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save manual entry', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const view = async (entry: BusinessManualRevenueEntry) => {
    setLoading(true);
    try {
      setDetail(await getBusinessManualRevenueEntry(entry.id, token));
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load entry history', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const submit = async (entry: BusinessManualRevenueEntry) => {
    if (!window.confirm('Submit this draft for maker-checker approval? Draft financial values cannot be edited after submission.')) return;
    await runDecision('Submit failed', async () => submitBusinessManualRevenueEntry(entry.id, token), 'Manual entry submitted');
  };

  const approve = async (entry: BusinessManualRevenueEntry) => {
    if (!window.confirm(`Approve ${formatMoney(entry.amount, entry.currency)} for ${entry.center.displayName}?`)) return;
    await runDecision('Approval failed', async () => approveBusinessManualRevenueEntry(entry.id, token), 'Manual entry approved');
  };

  const reject = async () => {
    if (!rejecting) return;
    const reason = cleanSpaces(rejectionReason);
    if (!reason) {
      setAlert({ type: 'error', title: 'Rejection reason required', message: 'Enter a clear reason before rejecting this manual entry.' });
      return;
    }
    await runDecision('Rejection failed', async () => rejectBusinessManualRevenueEntry(rejecting.id, reason, token), 'Manual entry rejected');
    setRejecting(null);
    setRejectionReason('');
  };

  const runDecision = async (failureTitle: string, operation: () => Promise<unknown>, successTitle: string) => {
    setLoading(true);
    try {
      await operation();
      setDetail(null);
      await load();
      setAlert({ type: 'success', title: successTitle, message: 'The workflow decision and immutable history were recorded.' });
    } catch (error) {
      setAlert({ type: 'error', title: failureTitle, message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const download = useCallback(() => exportCsv(
    'business-manual-revenue-entries.csv',
    ['Date', 'Centre Code', 'Centre', 'Source', 'Amount', 'Currency', 'Category', 'Reason', 'Reference', 'Attachment', 'Status', 'Maker', 'Checker', 'Rejection Reason'],
    entries.map((entry) => [
      dateOnly(entry.businessDate),
      entry.center.centerCode,
      entry.center.displayName,
      entry.revenueSource.sourceName,
      entry.amount,
      entry.currency,
      entry.adjustmentCategory,
      entry.reason,
      entry.externalReference,
      entry.attachmentReference,
      entry.status,
      entry.enteredBy,
      entry.approvedBy || entry.rejectedBy,
      entry.rejectionReason,
    ]),
  ), [entries]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(
      <div className="business-finance-actions revenue-operations-topbar-actions">
        <button className="btn compact-btn revenue-operations-topbar-action is-export" onClick={download} disabled={!entries.length}>Export Scoped CSV</button>
        {canManage && <button className="btn compact-btn revenue-operations-topbar-action is-create" aria-label="New Manual Entry" onClick={openCreate} disabled={!newEntryAvailable}>+ New Manual Entry</button>}
      </div>,
    );
    return undefined;
  }, [active, canManage, download, entries.length, newEntryAvailable, onTopBarActionsChange, openCreate]);

  if (!active) return null;

  return (
    <section className="tab-content business-source-page" id="tab-business-manual-revenue">
      {loading && <Loader message="Processing Manual Revenue Entries..." />}
      {!newEntryAvailable && <div className="business-finance-policy-banner"><strong>Manual adjustment entry is currently disabled.</strong><span>`MANUAL_ADJUSTMENT` must remain inactive until Finance approval is recorded.</span></div>}
      <div className="enterprise-card enterprise-table-card business-finance-queue manual-approval-queue-card">
        <div className="enterprise-card-heading"><div><strong>Approval Queue</strong><span>Only pending entries in your API-enforced centre scope are shown.</span></div><div className="business-finance-queue-total"><span>Pending totals by currency</span>{pendingTotals.length ? pendingTotals.map(([currency, total]) => <strong key={currency}>{formatMoney(total, currency)}</strong>) : <strong>No pending amounts</strong>}</div></div>
        <div className="enterprise-table-wrap"><table className="enterprise-table manual-revenue-table"><thead><tr><th>Date</th><th>Centre</th><th>Source</th><th>Amount</th><th>Reason / Evidence</th><th>Maker</th><th>Decision</th></tr></thead><tbody>
          {pending.map((entry) => {
            const selfDecision = entry.enteredBy === userName;
            return <tr key={entry.id}><td>{dateOnly(entry.businessDate)}</td><td>{entry.center.displayName}<small className="business-finance-subline">{entry.center.centerCode}</small></td><td>{entry.revenueSource.sourceName}</td><td><strong>{formatMoney(entry.amount, entry.currency)}</strong></td><td>{entry.reason}<small className="business-finance-subline">{entry.externalReference || entry.attachmentReference || 'No optional reference'}</small></td><td>{entry.enteredBy}</td><td><div className="business-finance-row-actions manual-revenue-actions"><button className="btn btn-sm revenue-operation-action is-audit" onClick={() => void view(entry)}>Audit</button><button className="btn btn-sm revenue-operation-action is-approve" disabled={!canApprove || selfDecision} title={selfDecision ? 'Maker cannot approve their own entry' : undefined} onClick={() => void approve(entry)}>Approve</button><button className="btn btn-sm revenue-operation-action is-reject" disabled={!canApprove || selfDecision} onClick={() => { setRejecting(entry); setRejectionReason(''); }}>Reject</button></div></td></tr>;
          })}
          {!pending.length && <tr><td colSpan={7} className="empty-cell">No pending manual entries in your permitted centres.</td></tr>}
        </tbody></table></div>
      </div>
      <div className="enterprise-card enterprise-table-card manual-revenue-history-card">
        <div className="business-finance-filters manual-revenue-filter-grid">
          <FinanceField label="Centre"><select value={centerCode} onChange={(event) => setCenterCode(event.target.value)}><option value="ALL">All permitted centres</option>{scopedCenters.map((center) => <option key={center.id} value={center.centerCode}>{center.displayName}</option>)}</select></FinanceField>
          <FinanceField label="Status"><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="ALL">All statuses</option>{['DRAFT', 'PENDING', 'APPROVED', 'REJECTED'].map((value) => <option key={value}>{label(value)}</option>)}</select></FinanceField>
          <FinanceField label="From"><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></FinanceField>
          <FinanceField label="To"><input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></FinanceField>
          <button className="btn revenue-operation-filter-refresh" onClick={() => void load()}>Refresh</button>
        </div>
        <div className="enterprise-table-wrap"><table className="enterprise-table manual-revenue-table"><thead><tr><th>Date</th><th>Centre</th><th>Category / Reason</th><th>Amount</th><th>Status</th><th>Maker / Checker</th><th>Actions</th></tr></thead><tbody>
          {entries.map((entry) => <tr key={entry.id}><td>{dateOnly(entry.businessDate)}</td><td>{entry.center.displayName}<small className="business-finance-subline">{entry.center.centerCode}</small></td><td><strong>{label(entry.adjustmentCategory)}</strong><small className="business-finance-subline">{entry.reason}</small></td><td>{formatMoney(entry.amount, entry.currency)}</td><td><FinanceStatus value={entry.status} /></td><td>{entry.enteredBy}<small className="business-finance-subline">{entry.approvedBy || entry.rejectedBy || 'Awaiting decision'}</small></td><td><div className="business-finance-row-actions manual-revenue-actions"><button className="btn btn-sm revenue-operation-action is-view" onClick={() => void view(entry)}>View</button>{canManage && entry.status === 'DRAFT' && <><button className="btn btn-sm revenue-operation-action is-edit" onClick={() => openEdit(entry)}>Edit</button><button className="btn btn-sm revenue-operation-action is-submit" onClick={() => void submit(entry)}>Submit</button></>}</div></td></tr>)}
          {!entries.length && <tr><td colSpan={7} className="empty-cell">No manual entries match the selected scoped filters.</td></tr>}
        </tbody></table></div>
      </div>
      {editing !== undefined && <EntryEditor editing={editing} form={form} setForm={setForm} centers={eligibleEntryCenters} sources={activeManualSources} onClose={() => setEditing(undefined)} onSave={() => void save()} />}
      {detail && <EntryDetail entry={detail} onClose={() => setDetail(null)} />}
      {rejecting && <FinanceModal title="Reject Manual Entry" subtitle={`${formatMoney(rejecting.amount, rejecting.currency)} — ${rejecting.center.displayName}`} onClose={() => setRejecting(null)} footer={<><span>This decision is terminal and audited.</span><div><button className="btn btn-outline" onClick={() => setRejecting(null)}>Cancel</button><button className="btn btn-primary" onClick={() => void reject()}>Confirm Rejection</button></div></>}><FinanceField label="Rejection Reason" full><textarea rows={4} value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} /></FinanceField></FinanceModal>}
      {alert && <AdminAlertModal title={alert.title} message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
    </section>
  );
}

function EntryEditor({ editing, form, setForm, centers, sources, onClose, onSave }: {
  editing: BusinessManualRevenueEntry | null;
  form: EntryForm;
  setForm: (updater: (current: EntryForm) => EntryForm) => void;
  centers: BusinessReportCenter[];
  sources: BusinessManualRevenueSourceOption[];
  onClose: () => void;
  onSave: () => void;
}) {
  return <FinanceModal title={editing ? 'Edit Manual Revenue Draft' : 'New Manual Revenue Draft'} subtitle="This creates a separate adjustment and never changes an imported fact." wide onClose={onClose} footer={<><span>All required values are validated again by the API.</span><div><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={onSave}>Save Draft</button></div></>}>
    <div className="business-source-form-grid">
      <FinanceField label="Business Date"><input type="date" value={form.businessDate} onChange={(event) => setForm((current) => ({ ...current, businessDate: event.target.value }))} /></FinanceField>
      <FinanceField label="Revenue Source"><select value={form.revenueSourceKey} onChange={(event) => setForm((current) => ({ ...current, revenueSourceKey: event.target.value, centerCode: '' }))}><option value="">Select active manual source</option>{sources.map((source) => <option key={source.sourceKey} value={source.sourceKey}>{source.sourceName}</option>)}</select></FinanceField>
      <FinanceField label="Centre" help={form.revenueSourceKey && !centers.length ? 'This source has no active centre in your permitted scope.' : undefined}><select value={form.centerCode} disabled={!form.revenueSourceKey || !centers.length} onChange={(event) => setForm((current) => ({ ...current, centerCode: event.target.value }))}><option value="">{centers.length ? 'Select permitted mapped centre' : 'No eligible centre'}</option>{centers.map((center) => <option key={center.id} value={center.centerCode}>{center.displayName}</option>)}</select></FinanceField>
      {form.revenueSourceKey && !centers.length && <div className="business-finance-policy-banner warning full"><strong>No eligible centre</strong><span>The selected source has no active mapping within your permitted centre scope. The API will reject an out-of-scope combination.</span></div>}
      <FinanceField label="Adjustment Category"><input value={form.adjustmentCategory} placeholder="For example CORRECTION" onChange={(event) => setForm((current) => ({ ...current, adjustmentCategory: event.target.value.toUpperCase() }))} /></FinanceField>
      <FinanceField label="Amount"><input inputMode="decimal" value={form.amount} placeholder="0.00" onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))} /></FinanceField>
      <FinanceField label="Currency" help="No default currency is assumed."><input maxLength={3} value={form.currency} placeholder="INR" onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))} /></FinanceField>
      <FinanceField label="Reason" full><textarea rows={3} value={form.reason} onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))} /></FinanceField>
      <FinanceField label="External Reference"><input value={form.externalReference} onChange={(event) => setForm((current) => ({ ...current, externalReference: event.target.value }))} /></FinanceField>
      <FinanceField label="Attachment Reference" help="Governed identifier/path only; no file content is stored here."><input value={form.attachmentReference} onChange={(event) => setForm((current) => ({ ...current, attachmentReference: event.target.value }))} /></FinanceField>
      <FinanceField label="Related Imported Fact UUID" help="Optional; must match the same centre and business date." full><input value={form.relatedRevenueFactId} onChange={(event) => setForm((current) => ({ ...current, relatedRevenueFactId: event.target.value }))} /></FinanceField>
    </div>
  </FinanceModal>;
}

function EntryDetail({ entry, onClose }: { entry: BusinessManualRevenueEntry; onClose: () => void }) {
  return <FinanceModal title="Manual Entry Audit" subtitle={`${entry.center.displayName} — ${dateOnly(entry.businessDate)}`} wide onClose={onClose} footer={<><span>Imported facts remain immutable and separate.</span><button className="btn btn-primary" onClick={onClose}>Close</button></>}>
    <div className="business-finance-detail-grid"><div><span>Status</span><FinanceStatus value={entry.status} /></div><div><span>Amount</span><strong>{formatMoney(entry.amount, entry.currency)}</strong></div><div><span>Category</span><strong>{label(entry.adjustmentCategory)}</strong></div><div><span>Source</span><strong>{entry.revenueSource.sourceName}</strong></div><div className="full"><span>Reason</span><strong>{entry.reason}</strong></div><div><span>Reference</span><strong>{entry.externalReference || '—'}</strong></div><div><span>Attachment</span><strong>{entry.attachmentReference || '—'}</strong></div><div><span>Maker</span><strong>{entry.enteredBy}</strong></div><div><span>Checker</span><strong>{entry.approvedBy || entry.rejectedBy || '—'}</strong></div></div>
    <h4>Immutable History</h4>
    <div className="enterprise-table-wrap"><table className="enterprise-table"><thead><tr><th>Revision</th><th>Action</th><th>Performed By</th><th>Time</th></tr></thead><tbody>{entry.history?.map((row) => <tr key={row.revision}><td>{row.revision}</td><td>{label(row.action)}</td><td>{row.performedBy}</td><td>{dateTime(row.performedAt)}</td></tr>)}{!entry.history?.length && <tr><td colSpan={4} className="empty-cell">No history revisions returned.</td></tr>}</tbody></table></div>
  </FinanceModal>;
}

function entryInput(form: EntryForm): BusinessManualRevenueEntryInput {
  const amount = form.amount.trim();
  if (!form.businessDate || !form.centerCode || !form.revenueSourceKey) throw new Error('Business date, centre and revenue source are required.');
  if (!/^-?\d{1,16}(?:\.\d{1,2})?$/.test(amount)) throw new Error('Amount must be a valid number with at most two decimal places.');
  const currency = cleanSpaces(form.currency).toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) throw new Error('Currency must be a three-letter code.');
  const category = cleanSpaces(form.adjustmentCategory).toUpperCase().replace(/[^A-Z0-9]+/g, '_');
  if (category.length < 3) throw new Error('Adjustment category is required.');
  const reason = cleanSpaces(form.reason);
  if (!reason) throw new Error('Reason is required.');
  return {
    businessDate: form.businessDate,
    centerCode: form.centerCode,
    revenueSourceKey: form.revenueSourceKey,
    amount,
    currency,
    adjustmentCategory: category,
    reason,
    externalReference: cleanSpaces(form.externalReference) || null,
    attachmentReference: cleanSpaces(form.attachmentReference) || null,
    relatedRevenueFactId: cleanSpaces(form.relatedRevenueFactId) || null,
  };
}
